import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { IdentityChip } from '../components/Avatar'
import { JustPulled } from '../components/JustPulled'
import { PackCard } from '../components/PackCard'
import { EmptyState, SectionHeading, Skeleton, TIER_ACCENT, TIER_LABEL } from '../components/ui'
import { useLeaderboard, usePacks, useSiteStats } from '../hooks/queries'
import type { PackSummary, Tier } from '../lib/api'
import { MAX_CARDS, MIN_CARDS } from '../lib/packs'
import { cents, multiple, percent, timeAgo, usd } from '../lib/format'

/** Slides rotate every 7s. Pause on hover, stop on focus. */
const SLIDE_MS = 7000

/**
 * One headline per tier, distinct from `pack.tagline` below it rather than a
 * summary of it — the eyebrow already names the tier, so this is the one line
 * that gets to say something the tile itself does not have room for.
 */
const HERO_HEADLINE: Record<Tier, string> = {
  starter: 'Real tokens, entry stakes',
  pro: 'Twice the stake, better odds',
  silver: 'Built to leave something behind',
  elite: 'Where the mythic band opens',
  gold: 'The cheap way into the long tail',
  diamond: 'The top of the ladder',
}

export function HomePage() {
  const { data: packs, isLoading } = usePacks()

  return (
    <div className="pb-8">
      <section className="pt-5">
        <Hero packs={packs} />
      </section>

      <section className="pt-10">
        <SectionHeading
          title="Six packs, eight lengths"
          sub={`Pick a tier, then set how many cards are in it, ${MIN_CARDS} to ${MAX_CARDS}. Odds and return-to-player are published before you pay.`}
          action={
            <Link to="/packs" className="btn btn-ghost btn-sm">
              Compare every tier
            </Link>
          }
        />

        {isLoading ? (
          /* Six tiles: three-up is the honest desktop layout, and only a
             genuinely wide screen has room to put the whole ladder on one row. */
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-[460px]" />
            ))}
          </div>
        ) : packs && packs.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
            {packs.map((pack) => (
              <PackCard key={pack.id} pack={pack} />
            ))}
          </div>
        ) : (
          <EmptyState title="No packs yet" body="The catalogue could not be loaded." />
        )}
      </section>

      <section className="pt-12">
        <SectionHeading
          title="Just pulled"
          sub="Rare and better, across every account on the site, as it happens."
        />
        <JustPulled />
      </section>

      <section className="pt-12">
        <BiggestRips />
      </section>

      <section className="pt-12">
        <HouseNumbers />
      </section>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Hero
 * ------------------------------------------------------------------ */

/**
 * One slide per tier, cheapest first. The carousel is not a way to fit a large
 * catalogue into one box — it *is* the catalogue, so every slide is a real
 * destination rather than a sample of one.
 */
function Hero({ packs }: { packs?: PackSummary[] }) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const slides = packs ?? []
  const active = slides.length > 0 ? slides[index % slides.length] : undefined

  useEffect(() => {
    if (paused || slides.length < 2) return
    const id = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), SLIDE_MS)
    return () => window.clearInterval(id)
  }, [paused, slides.length])

  if (!active) return <Skeleton className="h-[340px] sm:h-[420px]" />

  const accent = TIER_ACCENT[active.tier]
  const high = active.prices[MAX_CARDS] ?? active.price

  return (
    <div
      className="panel relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* One painted scene per tier rather than one shared backdrop tinted by
          gradient. Deliberately a single plain <img> whose `src` swaps: the
          crossfade this replaced kept two 2048x1152 images mounted and ran an
          opacity animation across them every seven seconds, and every extra
          composite and remount the carousel performs is work the shared pack
          renderer further down the page has to contend with. */}
      {/* 3.2:1 plates, and `object-right`.

          Dropping the chart column left this panel wide and short, while the
          generated art is 16:9, so cover-fitting the art raw zoomed in and ate
          the top and bottom of every picture. Each plate is widened to 3.2:1
          instead, by stretching the source's own left edge across the new
          space — flat dark ground by design, so the join is invisible and the
          artwork itself is untouched at full plate height, flush right.
          `object-right` pins the plate's right edge to the panel's, so the
          widened left padding is what a narrowing viewport eats first and the
          subject stays whole. That holds only while the panel is wide enough
          to still be showing some of that padding. Below `sm` it is not: the
          crop window is down to about a third of the plate and has passed the
          padding entirely, so `object-right` is now keeping the busiest part
          of the picture and nothing else. The scrim below is what covers that
          case, because no crop anchor can fix it.

          The artwork deliberately bleeds off the bottom and right. An inset
          version that floated it clear of both was tried and reverted: it read
          as smaller and more timid, and full bleed is what a banner wants.

          `-v3`: /art/ is cached 30 days with no content hash in the path, so a
          same-URL rewrite sits stale in Cloudflare and in every visitor's
          browser for a month. A new filename is a guaranteed miss everywhere. */}
      <img
        src={`/art/hero-${active.tier}-v3.jpg`}
        alt=""
        aria-hidden
        className="absolute inset-0 size-full object-cover object-right opacity-90"
      />
      {/* Much lighter than it needed to be for the painted scenes this
          replaced. Those were busy edge to edge, so the copy had to be rescued
          from whatever happened to sit under it; this art is composed for the
          slot, with the subject held to the right and flat dark ground on the
          left. The scrim only has to guarantee the left column, not dim the
          whole picture — which at the old weight it did, muting flat vector
          colour into sludge. */}
      <div
        className="absolute inset-0 hidden sm:block"
        style={{
          background:
            'linear-gradient(to right, rgb(0 0 0 / 0.62) 0%, rgb(0 0 0 / 0.2) 46%, transparent 68%)',
        }}
        aria-hidden
      />
      {/* Narrow: both of the premises above fail, so the scrim stops being
          directional.

          The copy runs the full width of the panel rather than a left column,
          so more than half of it sits past the 68% stop where the wide scrim
          has already faded to nothing. And the plate is 3.2:1 against a
          roughly square card, so only about a third of the image width
          survives the crop: with `object-right` the surviving slice is the
          densest part of the picture, bars and rocket, sitting directly under
          the headline and the stats.

          Shifting the crop does not rescue it either. The art occupies the
          right half of the plate and the left half is empty ground, so any
          anchor that keeps artwork on screen keeps it behind the text, and any
          anchor that clears the text shows a blank rectangle. There is no
          horizontal crop of a plate composed this way that puts flat ground
          under full-width copy. So the narrow scrim holds the whole picture
          back to a texture instead of trying to carve a safe column out of
          it, and stays near-flat top to bottom because the copy reaches from
          the eyebrow to the buttons with no gap to aim a falloff at. */}
      <div
        className="absolute inset-0 sm:hidden"
        style={{
          background:
            'linear-gradient(to bottom, rgb(0 0 0 / 0.88) 0%, rgb(0 0 0 / 0.8) 50%, rgb(0 0 0 / 0.88) 100%)',
        }}
        aria-hidden
      />
      {/* Single column. The right half used to hold a per-tier chart; the art
          behind it now carries that job, and the copy keeps its own width cap
          so it does not run the full width of the panel over the top of it. */}
      <div className="relative p-6 sm:p-10">
        <div
          className="max-w-2xl [text-shadow:0_2px_16px_rgb(0_0_0_/_0.85)]"
        >
          <div
            className="text-[0.6875rem] font-bold tracking-[0.18em] uppercase"
            style={{ color: accent }}
          >
            {TIER_LABEL[active.tier]} tier
          </div>
          {/* Both of these reserve two lines' worth of height whether or not
              they need it. The copy is per-tier and its length varies, so
              without a reserved box the panel grew and shrank on every slide
              and shoved the whole page below it up and down every 7 seconds.
              Held in `em` rather than pixels so it tracks the font size at
              each breakpoint instead of needing a value per breakpoint. */}
          <h1 className="mt-3 min-h-[2.1em] text-3xl leading-[1.05] font-bold tracking-tight sm:text-5xl">
            {HERO_HEADLINE[active.tier]}
          </h1>
          {/* Each tier's own pitch, not a shared line — same text `pack.tagline`
              prints on its shop tile, so the carousel can never say something
              about a tier that the tile underneath it disagrees with. */}
          <p
            className="mt-4 min-h-[3.25em] max-w-lg text-sm leading-relaxed"
            style={{ color: 'var(--color-muted)' }}
          >
            {active.tagline}
          </p>

          <dl className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
            <div>
              <dt className="eyebrow">{TIER_LABEL[active.tier]} from</dt>
              <dd className="tnum mt-0.5 text-lg font-bold">
                {usd(active.basePrice, { whole: true })}
              </dd>
            </div>
            <div>
              <dt className="eyebrow">Up to</dt>
              <dd className="tnum mt-0.5 text-lg font-bold">{usd(high, { whole: true })}</dd>
            </div>
            <div>
              <dt className="eyebrow">Published RTP</dt>
              <dd className="tnum mt-0.5 text-lg font-bold">{percent(active.rtp, 1)}</dd>
            </div>
          </dl>

          {/* Reserves one row of buttons. The CTA carries the tier name, so
              its width changes per slide, and at some viewports the pair wraps
              to a second row for the longer names and not the shorter ones —
              which oscillated the panel height, and the whole page under it,
              every time the carousel advanced. */}
          <div className="mt-7 flex min-h-[3.125rem] flex-wrap gap-2.5">
            {/* "Build your X pack", not "Build a X pack": the tier names cycle
                through Elite, and "a Elite" is the article bug you only ever
                notice in production. */}
            <Link to={`/pack/${active.id}`} className="btn btn-primary btn-lg">
              Build your {TIER_LABEL[active.tier]} pack
            </Link>
            <Link to="/fairness" className="btn btn-ghost btn-lg">
              How the odds work
            </Link>
          </div>
        </div>
      </div>

      <div className="relative flex gap-1.5 px-6 pb-5 sm:px-10">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            onClick={() => setIndex(i)}
            aria-label={`Show ${slide.name}`}
            aria-current={i === index}
            className="h-1 rounded-none transition-all"
            style={{
              width: i === index ? 32 : 16,
              background: i === index ? accent : 'var(--color-surface-3)',
            }}
          />
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Supporting sections
 * ------------------------------------------------------------------ */

function BiggestRips() {
  const { data: entries, isLoading } = useLeaderboard('week')

  if (isLoading) return <Skeleton className="h-56" />
  if (!entries || entries.length === 0) return null

  return (
    <>
      <SectionHeading
        title="Biggest rips this week"
        sub="Ranked by multiple, so a $25 pack returning 40× outranks a $1,000 pack returning 2×."
        action={
          <Link to="/leaderboard" className="btn btn-ghost btn-sm">
            Full leaderboard
          </Link>
        }
      />
      <div className="panel divide-y overflow-hidden">
        {entries.slice(0, 5).map((entry) => (
          <div key={entry.openId} className="flex items-center gap-4 px-4 py-3">
            <span className="tnum w-6 shrink-0 text-sm font-bold" style={{ color: 'var(--color-faint)' }}>
              {entry.rank}
            </span>
            <div className="min-w-0 flex-1">
              <IdentityChip identity={entry.by} size={22} bold className="text-sm" />
              <div className="truncate text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                {entry.packName} · {timeAgo(entry.openedAt)}
                {entry.topPull ? ` · ${entry.topPull.symbol}` : ''}
              </div>
            </div>
            <div className="text-right">
              <div className="tnum text-sm font-bold" style={{ color: 'var(--color-up)' }}>
                {multiple(entry.multiple)}
              </div>
              <div className="tnum text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                {cents(entry.totalCents)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

/**
 * The realised site-wide return, published next to the theoretical one.
 * A platform that prints its theoretical RTP and hides the realised one is
 * only telling half of it.
 */
function HouseNumbers() {
  const { data } = useSiteStats()
  if (!data || data.opens === 0) return null

  return (
    <div className="panel grid gap-6 p-6 sm:grid-cols-4">
      <div>
        <div className="eyebrow">Packs opened</div>
        <div className="tnum mt-1 text-2xl font-bold">{data.opens.toLocaleString()}</div>
      </div>
      <div>
        <div className="eyebrow">Staked</div>
        <div className="tnum mt-1 text-2xl font-bold">{cents(data.stakedCents, { compact: true })}</div>
      </div>
      <div>
        <div className="eyebrow">Returned</div>
        <div className="tnum mt-1 text-2xl font-bold">{cents(data.returnedCents, { compact: true })}</div>
      </div>
      <div>
        <div className="eyebrow">Realised RTP</div>
        <div className="tnum mt-1 text-2xl font-bold">
          {data.realisedRtp === null ? '-' : percent(data.realisedRtp, 1)}
        </div>
        <div className="mt-1 text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
          Actual, not published
        </div>
      </div>
    </div>
  )
}
